<template>
  <div class="breadcrumbs p-3">
    <a href="/">Αρχική</a> / <strong>Τα εισιτήριά μου</strong>
  </div>
  <div class="container-xxl pb-5">
    <div v-if="loading" class="d-flex justify-content-center"><img src="../assets/images/loading.gif" alt="Φόρτωση" width="32"></div>
    <div v-else-if="error">{{ error }}</div>
    

    <div
      v-else-if="groupedTickets.length === 0"
      class="empty-state d-flex flex-column align-items-center justify-content-center text-center"
    >
      <h4 class="mb-2">Δεν βρέθηκαν εισιτήρια</h4>
      <p class="text-muted">
        Δεν υπάρχουν διαθέσιμα εισιτήρια στον λογαριασμό σας αυτή τη στιγμή.
      </p>
    </div>
    

    <div v-else class="row mt-3">
      
      <div
        v-for="event in groupedTickets"
        :key="event.eventTitle"
        class="event-section mb-5"
      >
        <h3 class="event-title mb-3">{{ event.eventTitle }}</h3>
        <div class="event-image-wrap">
            <img class="event-image" :src="event.eventImg">
        </div>

        <div
          v-for="dateEntry in event.dates"
          :key="dateEntry.date"
          class="mb-4"
        >
          <h5 class="date-heading mb-3">
            Ημερομηνία: {{ dateEntry.date }}
          </h5>

          <div class="row">
            <div
              class="col-12 col-sm-6 col-lg-4 mb-3"
              v-for="ticket in dateEntry.tickets"
              :key="ticket.id + ticket.code"
            >
              <div
                class="ticket-card card p-3 text-center"
                :class="{ expired: isExpired(ticket.date) || ticket.used }"

              >
                <canvas
                  :ref="el => generateQr(el, ticket.code)"
                  class="qr mb-2"
                ></canvas>
                <p class="time">Ώρα: {{ ticket.time }}</p>
                <p class="time">Ζώνη: {{ ticket.zone }}</p>
                <p v-if="ticket.discount && ticket.discount_title" class="time discount-text">Ειδική κατηγορία: {{ ticket.discount_title }}: {{ ticket.discount }}%</p>
                <p v-else class="time placeholder">-</p>

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { fetchTickets } from '@/services/user'

const tickets = ref([])
const loading = ref(true)
const error = ref(null)

/* ---------- helpers ---------- */

const parseDate = (dateStr) => {
  const [d, m, y] = dateStr.split('/').map(Number)
  return new Date(2000 + y, m - 1, d)
}

const isExpired = (dateStr) => {
  return parseDate(dateStr) < new Date()
}

const generateQr = (canvasEl, code) => {
  if (!canvasEl || !code || !window.QRCode) return
  window.QRCode.toCanvas(canvasEl, code, { width: 150 })
}

/* ---------- grouping + sorting ---------- */

const groupedTickets = computed(() => {
  const map = {}

  tickets.value.forEach(t => {
    const eventYear = parseDate(t.date).getFullYear()
    const groupKey = `${t.event_title}__${eventYear}`

    if (!map[groupKey]) {
      map[groupKey] = {
        eventTitle: t.event_title,
        eventImg: t.event_img,
        year: eventYear,
        dates: {}
      }
    }

    if (!map[groupKey].dates[t.date]) {
      map[groupKey].dates[t.date] = {
        date: t.date,
        tickets: []
      }
    }

    map[groupKey].dates[t.date].tickets.push(t)
  })

  const events = Object.values(map).map(event => {
    event.dates = Object.values(event.dates).sort(
      (a, b) => parseDate(b.date) - parseDate(a.date)
    )
    
    return event
  })

  // sort events by latest date DESC
  events.sort(
    (a, b) => parseDate(b.dates[0].date) - parseDate(a.dates[0].date)
  )

  return events
})


/* ---------- fetch ---------- */

onMounted(async () => {
  try {
    loading.value = true
    const res = await fetchTickets()
    tickets.value = res.data
  } catch (e) {
    console.error(e)
    error.value = 'Failed to load tickets'
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
  .card {
    border: 1px solid #ddd;
    border-radius: 8px;
  }
  canvas {
    margin: 10px auto;
    display: block;
  }

  .title,
  .date,
  .time{
    font-weight: 700;
    font-size: 14px;
  }

  .title{
    font-size: 18px;
  }

  .event-header {
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    text-align: center;
  }

  .event-image-wrapper {
    width: 100%;
    max-width: 600px;
    height: 160px;           
    margin: 0 auto;
    border-radius: 12px;
    overflow: hidden;       
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f3f3f3;     
  }

  .event-image {
    width: 100%;
    height: 100%;
    object-fit: cover;       
    object-position: center; 
  }


  .event-title {
    font-size: 20px;
    font-weight: 700;
  }

  .date-heading {
    font-weight: 500;
    background: #2b2b2b;
    width: fit-content;
    color: white;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 16px;
  }

  .ticket-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    height: -webkit-fill-available;
  }

  .qr {
    width: 140px;
    margin: 0 auto;
  }

  .time {
    font-weight: 600;
    margin-bottom: 0;
  }

 .expired {
    opacity: 0.45;
    filter: grayscale(1);
  }


  .event-image-wrap{
    height: 250px;
    margin-bottom: 20px;
  }

  .event-section{
    width: 100%;
    max-width: 1050px;
    margin: auto;
    border-bottom:2px dashed #af00362b;
  }

  .placeholder{
    visibility: hidden;
  }
  .empty-state {
    min-height: 300px;
    
  }
  .empty-state h4 {
    font-weight: 700;
  }


  .discount-text{
    color: orangered
  }

</style>
